import React, { useState } from 'react';
import { Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { apiClient } from '../publicApiClient';
import { spaceDetailPagePath } from '../utils/publicSpaceResolution';

export interface SpaceDetailLinkProps {
  spaceId?: string | null;
  spaceName: string;
  spaceCity?: string | null;
  variant?: 'h6' | 'subtitle1' | 'body1' | 'body2';
  color?: string;
}

const SpaceDetailLink: React.FC<SpaceDetailLinkProps> = ({
  spaceId,
  spaceName,
  spaceCity,
  variant = 'subtitle1',
  color = 'text.secondary',
}) => {
  const navigate = useNavigate();
  const [resolving, setResolving] = useState(false);
  const trimmedName = spaceName.trim();
  if (!trimmedName) return null;

  const city = spaceCity?.trim();
  const directPath = city ? spaceDetailPagePath(city, trimmedName) : null;
  const canResolve = Boolean(spaceId?.trim());

  const linkSx = {
    color: 'primary.main',
    fontWeight: 600,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    cursor: resolving ? 'wait' : 'pointer',
  } as const;

  if (directPath) {
    return (
      <MuiLink component={RouterLink} to={directPath} variant={variant} underline="always" sx={linkSx}>
        {trimmedName}
      </MuiLink>
    );
  }

  if (canResolve) {
    return (
      <MuiLink
        component="button"
        type="button"
        variant={variant}
        underline="always"
        disabled={resolving}
        onClick={async () => {
          const id = spaceId?.trim();
          if (!id || resolving) return;
          setResolving(true);
          try {
            const space = await apiClient.getSpaceSummary(id);
            navigate(spaceDetailPagePath(space.city, space.name || trimmedName));
          } finally {
            setResolving(false);
          }
        }}
        sx={{
          ...linkSx,
          background: 'none',
          border: 0,
          padding: 0,
          font: 'inherit',
        }}
      >
        {trimmedName}
      </MuiLink>
    );
  }

  return (
    <Typography variant={variant} color={color}>
      {trimmedName}
    </Typography>
  );
};

export default SpaceDetailLink;
